import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Check, ArrowRight } from 'lucide-react';

const plans = [
  {
    name: 'Essentiel',
    priceTTC: 348,
    monthlyTTC: 34.80,
    badge: null,
    desc: 'Une présence professionnelle simple et accessible.',
    features: [
      'Site vitrine d\'une page',
      'Design responsive',
      'Présentation de votre activité',
      'Coordonnées, horaires et itinéraire',
      'Boutons d\'appel et de contact',
      'Référencement technique de base',
      'Hébergement et maintenance inclus',
    ],
    cta: 'Choisir Essentiel',
    accent: false,
  },
  {
    name: 'Business',
    priceTTC: 588,
    monthlyTTC: 46.80,
    badge: 'Recommandé',
    desc: 'Le meilleur équilibre pour les artisans, commerçants et TPE.',
    features: [
      'Site vitrine de 3 à 5 pages',
      'Design personnalisé',
      'Présentation détaillée des prestations',
      'Formulaire de contact / devis',
      'Galerie de réalisations',
      'Référencement local de base',
      'Accompagnement à la mise en ligne',
      'Hébergement, maintenance et assistance',
    ],
    cta: 'Choisir Business',
    accent: true,
  },
  {
    name: 'Premium',
    priceTTC: 1188,
    monthlyTTC: 70.80,
    badge: null,
    desc: 'Une présence web complète et personnalisée.',
    features: [
      'Site vitrine jusqu\'à 8 pages',
      'Design approfondi sur-mesure',
      'Architecture de contenu adaptée',
      'Galerie avancée / portfolio',
      'Référencement initial renforcé',
      'Intégrations complémentaires',
      'Accompagnement complet',
      'Hébergement, maintenance et assistance',
    ],
    cta: 'Demander un devis Premium',
    accent: false,
  },
];

const PricingPage = () => {
  return (
    <div className="min-h-screen pt-24 overflow-x-hidden" data-testid="pricing-page">
      <SEO
        title="Tarifs - Création de sites internet"
        description="Nos formules de création de sites internet pour artisans, commerçants et TPE. À partir de 348€ TTC. Devis gratuit."
        path="/tarifs"
      />

      {/* Hero */}
      <section className="pt-8 pb-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto text-center">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="section-label mb-4">
            Nos tarifs
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6"
            data-testid="pricing-title"
          >
            Des offres <span className="text-[#8B5A8B]">claires et accessibles</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-base md:text-lg text-white/50 max-w-2xl mx-auto"
          >
            Choisissez la formule adaptée à votre activité et votre budget. Contactez-nous pour démarrer votre projet.
          </motion.p>
        </div>
      </section>

      {/* Plans */}
      <section className="px-6 lg:px-12 pb-24">
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.12 }}
              className={`glass-card rounded-2xl p-8 relative flex flex-col ${plan.accent ? 'border-[#8B5A8B]/40 shadow-[0_0_40px_rgba(139,90,139,0.15)]' : ''}`}
              data-testid={`plan-card-${plan.name.toLowerCase()}`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#8B5A8B] text-white text-xs font-semibold px-4 py-1 rounded-full">
                  {plan.badge}
                </div>
              )}
              <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
              <p className="text-white/40 text-sm mb-5">{plan.desc}</p>

              <div className="mb-1">
                <span className="text-4xl font-black text-white">{plan.priceTTC}</span>
                <span className="text-white/50 text-lg ml-1">€ TTC</span>
              </div>
              <p className="text-[#8B5A8B] text-sm font-medium mb-6">puis {plan.monthlyTTC} € TTC/mois</p>

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5">
                    <Check className="text-[#8B5A8B] flex-shrink-0 mt-0.5" size={15} />
                    <span className="text-white/60 text-sm">{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/contact"
                className={plan.accent ? 'glow-btn text-center text-sm w-full inline-flex items-center justify-center gap-2' : 'outline-btn text-center text-sm w-full inline-flex items-center justify-center gap-2'}
                data-testid={`plan-cta-${plan.name.toLowerCase()}`}
              >
                {plan.cta}
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
